import {
    getStampPerpendicularScatter,
    getStampRotationDegrees,
    stampPlacementTransform,
    stampObjectScaleFactor,
    type ActiveStampStroke,
    type ArtbrushStamp,
    type ParsedStampObject,
    type StampRenderOptions,
    type StampStroke,
} from "../../utils/artbrushStampBrush";
import {
    paletteColorIndexForPlacement,
    randomHexColorForPlacement,
} from "../../utils/pathPopulatePlacement";
import { paletteColorForPathIndex } from "../../utils/svgUtils";

function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;
    return svgContent.replace(
        elementRegex,
        (match, tagName, attributes, selfClose) => {
            const cleanedAttributes = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

function stampFillColor(
    renderOptions: StampRenderOptions,
    pathIndex: number,
    stampIndex: number,
): string | null {
    if (!renderOptions.colorEnabled) return null;
    if (renderOptions.colorMode === "single") return renderOptions.colorSingle;
    if (renderOptions.colorMode === "random-per-instance") {
        return randomHexColorForPlacement(pathIndex, stampIndex);
    }
    if (
        renderOptions.colorMode === "random-palette-per-instance" &&
        renderOptions.colorPalette.length > 0
    ) {
        const pi = paletteColorIndexForPlacement(
            pathIndex,
            stampIndex,
            renderOptions.colorPalette.length,
        );
        return (
            renderOptions.colorPalette[pi] ?? renderOptions.colorPalette[0] ?? null
        );
    }
    if (
        renderOptions.colorMode === "random-per-stroke" &&
        renderOptions.colorPalette.length > 0
    ) {
        return paletteColorForPathIndex(renderOptions.colorPalette, pathIndex);
    }
    return null;
}

export function ArtbrushStampLayer({
    stampStrokes,
    activeStampStroke,
    objects,
    renderOptions,
}: {
    stampStrokes: StampStroke[];
    activeStampStroke: ActiveStampStroke | null;
    objects: ParsedStampObject[];
    renderOptions: StampRenderOptions;
}) {
    if (objects.length === 0) return null;
    const hasUnion = stampStrokes.some((s) => s.unionPathD);
    const hasStamps =
        stampStrokes.some((s) => !s.unionPathD && s.stamps.length > 0) ||
        (activeStampStroke?.stamps.length ?? 0) > 0;
    if (!hasUnion && !hasStamps) return null;

    return (
        <g className="artbrush-svg-canvas__stamps">
            {stampStrokes.map((stroke, pathIndex) =>
                stroke.unionPathD ? (
                    <path
                        key={stroke.id}
                        className="artbrush-stamp-union"
                        d={stroke.unionPathD}
                        fill={stroke.unionFill ?? "#000000"}
                    />
                ) : (
                    stroke.stamps.map((stamp, stampIndex) => (
                        <StampInstance
                            key={stamp.id}
                            stamp={stamp}
                            stampIndex={stampIndex}
                            pathIndex={pathIndex}
                            objects={objects}
                            renderOptions={renderOptions}
                        />
                    ))
                ),
            )}
            {activeStampStroke?.stamps.map((stamp, stampIndex) => (
                <StampInstance
                    key={stamp.id}
                    stamp={stamp}
                    stampIndex={stampIndex}
                    pathIndex={stampStrokes.length}
                    objects={objects}
                    renderOptions={renderOptions}
                />
            ))}
        </g>
    );
}

function StampInstance({
    stamp,
    stampIndex,
    pathIndex,
    objects,
    renderOptions,
}: {
    stamp: ArtbrushStamp;
    stampIndex: number;
    pathIndex: number;
    objects: ParsedStampObject[];
    renderOptions: StampRenderOptions;
}) {
    const object = objects[stamp.objectIndex] ?? objects[0];
    if (!object) return null;
    const scale =
        stampObjectScaleFactor(object.dimensions, renderOptions.objectSize) *
        Math.max(0.05, stamp.sizeFactor || 1);
    const rotationDeg = getStampRotationDegrees(
        stamp,
        stampIndex,
        pathIndex,
        object,
        renderOptions,
    );
    const transform = stampPlacementTransform(
        stamp,
        scale,
        object.dimensions,
        renderOptions.anchor,
        rotationDeg,
        getStampPerpendicularScatter(
            stamp,
            pathIndex,
            stampIndex,
            renderOptions.perpendicularScatterPx,
        ),
    );
    return (
        <g
            transform={transform}
            dangerouslySetInnerHTML={{
                __html: (() => {
                    const fillColor = stampFillColor(
                        renderOptions,
                        pathIndex,
                        stampIndex,
                    );
                    return fillColor
                        ? applyFillToSvgContent(object.innerContent, fillColor)
                        : object.innerContent;
                })(),
            }}
        />
    );
}
